import { useEffect, useState, useCallback } from "react";
import { Mail, MessageCircle, Eye, CreditCard } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";

const TEMPLATES = [
  ["share_due", "Weekly Share Due"], ["share_reminder", "Payment Reminder"],
  ["weekly_report", "Weekly Report"], ["loss_recovery", "Loss Recovery Update"], ["custom", "Custom Message"],
];
const PAY_FIELDS = [["upi", "UPI ID"], ["bank_name", "Bank"], ["account_name", "Account Name"], ["account_no", "Account No"], ["ifsc", "IFSC"], ["note", "Note"]];

export const MessagePanel = ({ accountId, client, weeks, initialWeek }) => {
  const [form, setForm] = useState({ template: "share_due", week_start: initialWeek || weeks?.[0]?.week_start || "", custom_text: "" });
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [pay, setPay] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadHistory = useCallback(async () => {
    try { setHistory((await api.get(`/manager/mt5-clients/${accountId}/messages`)).data); } catch { /* ignore */ }
  }, [accountId]);
  useEffect(() => { loadHistory(); api.get("/manager/payment-details").then((r) => setPay(r.data)).catch(() => setPay({})); }, [loadHistory]);
  useEffect(() => { if (initialWeek) setForm((f) => ({ ...f, week_start: initialWeek })); }, [initialWeek]);

  const doPreview = async () => {
    try { setPreview((await api.post(`/manager/mt5-clients/${accountId}/message/preview`, { channel: "email", ...form })).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
  };
  const send = async (channel) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/manager/mt5-clients/${accountId}/message`, { channel, ...form });
      if (channel === "whatsapp") { window.open(data.wa_link, "_blank", "noopener"); toast.success("WhatsApp opened with the message pre-filled"); }
      else toast.success(`Email sent to ${data.to}`);
      loadHistory();
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };
  const savePay = async (e) => {
    e.preventDefault();
    try { setPay((await api.put("/manager/payment-details", pay)).data); toast.success("Payment details saved"); }
    catch { toast.error("Save failed"); }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_340px] gap-5" data-testid="message-panel">
      <div className="space-y-4">
        <div className="card-dark p-5 space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <div><label className="label-xs block mb-1">Template</label>
              <select data-testid="msg-template" className="input-dark" value={form.template} onChange={(e) => setForm({ ...form, template: e.target.value })}>
                {TEMPLATES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select></div>
            <div><label className="label-xs block mb-1">Week (auto-fills share amount)</label>
              <select data-testid="msg-week" className="input-dark" value={form.week_start} onChange={(e) => setForm({ ...form, week_start: e.target.value })}>
                <option value="">— none —</option>
                {(weeks || []).map((w) => <option key={w.week_start} value={w.week_start}>{w.week_start} → {w.week_end} · share ${w.share_amount} · {w.status}</option>)}
              </select></div>
          </div>
          <div><label className="label-xs block mb-1">{form.template === "custom" ? "Message" : "Extra note (optional)"}</label>
            <textarea data-testid="msg-custom" rows={3} className="input-dark" value={form.custom_text} onChange={(e) => setForm({ ...form, custom_text: e.target.value })} placeholder="Type anything extra you want to add…" /></div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="msg-preview-btn" onClick={doPreview} className="btn-outline px-3 py-2 text-xs flex items-center gap-2"><Eye size={12} /> Preview</button>
            <button data-testid="msg-send-email" disabled={busy || !client?.email} onClick={() => send("email")} className="btn-primary px-3 py-2 text-xs flex items-center gap-2 disabled:opacity-40"><Mail size={12} /> Send Email {client?.email ? `→ ${client.email}` : "(no email)"}</button>
            <button data-testid="msg-send-whatsapp" disabled={busy || !client?.phone} onClick={() => send("whatsapp")} className="px-3 py-2 text-xs rounded-lg flex items-center gap-2 bg-[#25D366]/15 border border-[#25D366]/40 text-[#25D366] disabled:opacity-40"><MessageCircle size={12} /> WhatsApp {client?.phone || "(no phone)"}</button>
          </div>
          {preview && (
            <div className="bg-[#050505] rounded-lg p-4 border border-white/5" data-testid="msg-preview">
              <p className="text-xs text-[#00F0FF] mb-2">{preview.subject}</p>
              <pre className="text-xs text-gray-300 whitespace-pre-wrap font-sans leading-relaxed">{preview.text}</pre>
            </div>
          )}
        </div>
        <div className="card-dark p-5">
          <p className="label-xs mb-3">Message History</p>
          {!history.length ? <p className="text-xs text-gray-500" data-testid="msg-history-empty">No messages sent yet.</p> : (
            <div className="space-y-2">
              {history.map((m) => (
                <div key={m.msg_id} className="flex items-start justify-between gap-3 border-b border-white/5 pb-2 text-xs" data-testid={`msg-history-${m.msg_id}`}>
                  <div><p className="text-gray-200">{m.subject}</p><p className="text-gray-600">{m.channel.toUpperCase()} → {m.to} · {String(m.created_at).slice(0, 16).replace("T", " ")}</p></div>
                  <span className={`text-[10px] uppercase ${m.status === "failed" ? "text-[#FF3B30]" : "text-[#00E676]"}`}>{m.status.replace("_", " ")}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <form onSubmit={savePay} className="card-dark p-5 space-y-3 h-fit" data-testid="payment-details-form">
        <p className="label-xs flex items-center gap-2"><CreditCard size={12} /> Your Payment Details (auto-inserted in messages)</p>
        {pay && PAY_FIELDS.map(([k, l]) => (
          <div key={k}><label className="text-[10px] text-gray-500 block mb-1">{l}</label>
            <input data-testid={`pay-${k}`} className="input-dark !py-1.5 text-xs" value={pay[k] || ""} onChange={(e) => setPay({ ...pay, [k]: e.target.value })} /></div>
        ))}
        <button data-testid="pay-save" className="btn-outline w-full py-2 text-xs">Save Payment Details</button>
      </form>
    </div>
  );
};
